import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Badge as SamlIcon,
  Link as LinkIcon,
  Info as InfoIcon,
  CloudDownload as ImportIcon,
  UploadFile as UploadFileIcon,
  Autorenew as AutorenewIcon,
  Fingerprint as FingerprintIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { motion } from 'motion/react';
import { toast } from 'react-toastify';
import {
  generatePreviewAlias,
  getSamlACSURL,
  getSamlSPEntityID,
  getSamlSPDescriptorURL,
} from '../../utils/constants';
import {
  MAX_METADATA_BYTES,
  SAML_DEFAULTS,
  SAML_NAMEID_FORMATS,
  NAMEID_FORMAT_EMAIL_ADDRESS,
  looksLikeSamlMetadata,
  mapImportedRequestToFormData,
  buildSamlPayload,
  describeImportFailure,
  certificateFingerprints,
} from '../../utils/samlMetadata';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';
import SamlConnectionDetails, { CopyRow } from './SamlConnectionDetails';

// SAML 2.0 accent color - Violet
const accentColor = '#7B5EA7';
const accentColorLight = '#F1ECF9';
const accentColorHover = '#674C8C';

const PRINCIPAL_TYPE_SUBJECT = 'SUBJECT';

/*
 * The three values Keycloak's SamlPrincipalType actually accepts. ATTRIBUTE_NAME
 * is deliberately absent: Keycloak swallows the parse failure and silently falls
 * back to SUBJECT, which produces opaque pairwise GUIDs as usernames with no log
 * line to explain it.
 */
const SAML_PRINCIPAL_TYPES = [
  { value: PRINCIPAL_TYPE_SUBJECT, label: 'Subject NameID' },
  { value: 'ATTRIBUTE', label: 'Attribute (by name)' },
  { value: 'FRIENDLY_ATTRIBUTE', label: 'Attribute (by friendly name)' },
];

/* RSA_SHA256 is the only algorithm Entra ID verifies; the other two exist for
 * older ADFS deployments. */
const SAML_SIGNATURE_ALGORITHMS = ['RSA_SHA256', 'RSA_SHA512', 'RSA_SHA1'];

const CONFIGURATION_SOURCES = [
  { value: 'url', label: 'Metadata URL' },
  { value: 'xml', label: 'Metadata XML file' },
  { value: 'manual', label: 'Manual entry' },
];

/** Byte length, not character count -- MAX_METADATA_BYTES mirrors a Go byte cap
 *  and a metadata document with non-ASCII organisation names is longer on the
 *  wire than in the textarea. */
const byteLength = (text) => {
  const value = typeof text === 'string' ? text : '';
  if (typeof TextEncoder !== 'undefined') {
    try {
      return new TextEncoder().encode(value).length;
    } catch (e) {
      // fall through to the character count
    }
  }
  return value.length;
};

const METADATA_LIMIT_MESSAGE = `The metadata document exceeds the ${Math.floor(
  MAX_METADATA_BYTES / 1024
)} KB limit. Metadata this large is almost always an aggregate feed rather than a single identity provider.`;

/** Keycloak's config map is string-valued, so a boolean round-trips as "true" /
 *  "false". A Switch needs a real boolean or it becomes uncontrolled. */
const configBool = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) === 'true';
};

const isAttributePrincipal = (principalType) => principalType !== PRINCIPAL_TYPE_SUBJECT;

const SamlIdPList = ({
  samlIdPs = [],
  samlIdPsLimit = 10,
  createSamlIdP,
  updateSamlIdP,
  deleteSamlIdP,
  listSamlIdPs,
  importSamlMetadata,
  loading,
  isInitialLoading = false,
  isReadOnly: isReadOnlyProp = false,
}) => {
  const isReadOnlyHook = useReadOnlyMode();
  const isReadOnly = isReadOnlyProp || isReadOnlyHook;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdP, setEditingIdP] = useState(null);
  const [formData, setFormData] = useState({ ...SAML_DEFAULTS });
  const [formError, setFormError] = useState(null);

  const [sourceMode, setSourceMode] = useState('url');
  // Import runs outside the hook's global `loading`, so it needs its own flag.
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importInfo, setImportInfo] = useState(null);

  const [fingerprints, setFingerprints] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);

  const [detailsIdP, setDetailsIdP] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsJustCreated, setDetailsJustCreated] = useState(false);

  // Thumbprints are recomputed from whatever is in the field, so a pasted PEM
  // shows a chip the admin can compare against Entra before saving.
  useEffect(() => {
    let cancelled = false;
    const certificate = formData.signingCertificate;

    if (!certificate || !certificate.trim()) {
      setFingerprints([]);
      return undefined;
    }

    certificateFingerprints(certificate).then((values) => {
      if (!cancelled) setFingerprints(values);
    });

    return () => {
      cancelled = true;
    };
  }, [formData.signingCertificate]);

  // Pre-create these URLs are a PREVIEW: the alias is minted server-side and
  // GenerateUniqueAlias appends -1/-2 on collision. SamlConnectionDetails, opened
  // straight after a successful create, is the authoritative copy.
  const previewAlias = editingIdP ? editingIdP.alias : generatePreviewAlias(formData.displayName, 'saml');
  const editingServiceProvider = editingIdP?.serviceProvider || {};
  const dialogAcsUrl = editingServiceProvider.acsUrl || getSamlACSURL(previewAlias);
  const dialogSpEntityId = formData.entityId.trim() || editingServiceProvider.entityId || getSamlSPEntityID();
  const dialogDescriptorUrl = editingServiceProvider.metadataDescriptorUrl || getSamlSPDescriptorURL(previewAlias);

  const resetForm = () => {
    setFormData({ ...SAML_DEFAULTS });
    setFormError(null);
    setSourceMode('url');
    setImportError(null);
    setImportInfo(null);
    setFingerprints([]);
    setCopiedKey(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setEditingIdP(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (idp) => {
    const cfg = idp.config || {};

    setFormData({
      ...SAML_DEFAULTS,
      displayName: idp.displayName || '',

      // Import scratch fields always start empty on an edit.
      metadataUrl: '',
      metadataXml: '',

      entityId: cfg.entityId || '',
      idpEntityId: cfg.idpEntityId || '',
      singleSignOnServiceUrl: cfg.singleSignOnServiceUrl || '',
      // Prefilled UNMASKED and always sent back. Unlike an OIDC client secret,
      // Keycloak does not mask a certificate on read, so a real round trip is
      // possible -- and a blank field on save is exactly how the cert-wipe defect
      // happens (Keycloak's PUT is a full replace and removeEmptyString() deletes
      // the key, leaving validateSignature true with nothing to validate against).
      signingCertificate: cfg.signingCertificate || '',
      metadataDescriptorUrl: cfg.metadataDescriptorUrl || '',

      nameIDPolicyFormat: cfg.nameIDPolicyFormat || SAML_DEFAULTS.nameIDPolicyFormat,
      principalType: cfg.principalType || SAML_DEFAULTS.principalType,
      principalAttribute: cfg.principalAttribute || '',
      // List responses omit emailAttributeName to avoid an N+1 mapper lookup, so
      // fall back to the same default the backend applies when it is blank.
      emailAttributeName: idp.emailAttributeName || SAML_DEFAULTS.emailAttributeName,

      signatureAlgorithm: cfg.signatureAlgorithm || SAML_DEFAULTS.signatureAlgorithm,
      validateSignature: configBool(cfg.validateSignature, SAML_DEFAULTS.validateSignature),
      wantAssertionsSigned: configBool(cfg.wantAssertionsSigned, SAML_DEFAULTS.wantAssertionsSigned),
      wantAuthnRequestsSigned: configBool(cfg.wantAuthnRequestsSigned, SAML_DEFAULTS.wantAuthnRequestsSigned),
      forceAuthn: configBool(cfg.forceAuthn, SAML_DEFAULTS.forceAuthn),
      postBindingResponse: configBool(cfg.postBindingResponse, SAML_DEFAULTS.postBindingResponse),
      postBindingAuthnRequest: configBool(cfg.postBindingAuthnRequest, SAML_DEFAULTS.postBindingAuthnRequest),
      allowedClockSkew:
        cfg.allowedClockSkew === undefined || cfg.allowedClockSkew === ''
          ? SAML_DEFAULTS.allowedClockSkew
          : cfg.allowedClockSkew,

      enabled: idp.enabled !== false,
    });

    setFormError(null);
    setSourceMode('manual');
    setImportError(null);
    setImportInfo(null);
    setCopiedKey(null);
    setEditingIdP(idp);
    setDialogOpen(true);
  };

  const handleClose = () => {
    // An import is a server round trip that will write into formData when it
    // lands; closing underneath it would drop the result silently.
    if (importing) return;

    setDialogOpen(false);
    setEditingIdP(null);
    resetForm();
  };

  const handleOpenDetails = (idp) => {
    setDetailsIdP(idp);
    setDetailsJustCreated(false);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsJustCreated(false);
    setDetailsIdP(null);
  };

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // No filterInput anywhere in this dialog. GENERAL_TEXT strips + / = and would
  // destroy base64; URL has no \s and mangles every multi-line PEM. The existing
  // live IdP dialogs do not sanitise either -- the backend's character gate and
  // x509.ParseCertificate are the real defence.
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Capped on CHANGE as well as on file pick and on submit: a paste into the
    // textarea reaches neither of the other two gates.
    if (name === 'metadataXml' && byteLength(value) > MAX_METADATA_BYTES) {
      setImportError(METADATA_LIMIT_MESSAGE);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Switching source clears the import banners but never formData: an admin who
  // imported and then switched to Manual entry to touch up one field must not
  // lose everything the import filled in.
  const handleSourceChange = (e) => {
    setSourceMode(e.target.value);
    setImportError(null);
    setImportInfo(null);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_METADATA_BYTES) {
      setImportError(METADATA_LIMIT_MESSAGE);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, metadataXml: String(reader.result) }));
      setImportError(null);
      setImportInfo(null);
    };
    reader.onerror = () => setImportError('Could not read the selected file.');
    reader.readAsText(file);

    // Reset the input so re-selecting the same file fires onChange again
    // (readAsText has already captured the file; clearing is safe here).
    e.target.value = '';
  };

  const applyImportResult = (response, mode) => {
    const { next, filled, warnings } = mapImportedRequestToFormData(response?.request, formData);
    setFormData(next);
    setImportInfo({
      mode,
      filled,
      warnings: [...(response?.warnings || []), ...warnings],
    });
  };

  const handleImport = async () => {
    setImportError(null);
    setImportInfo(null);

    const mode = sourceMode === 'xml' ? 'xml' : 'url';
    let request;

    if (mode === 'url') {
      const url = (formData.metadataUrl || '').trim();
      if (!url) {
        setImportError('Enter the metadata URL published by your identity provider, then choose Import.');
        return;
      }
      // Inline pre-flight. The backend refuses anything but https anyway (and
      // guards the fetch against SSRF), but a local answer is instant.
      if (!/^https:\/\//i.test(url)) {
        setImportError('The metadata URL must start with https://. Prism will not fetch metadata over plaintext HTTP.');
        return;
      }
      request = { metadataUrl: url };
    } else {
      const xml = formData.metadataXml || '';
      if (!xml.trim()) {
        setImportError('Upload or paste a metadata document, then choose Import.');
        return;
      }
      if (byteLength(xml) > MAX_METADATA_BYTES) {
        setImportError(METADATA_LIMIT_MESSAGE);
        return;
      }
      if (!looksLikeSamlMetadata(xml)) {
        setImportError(
          'This document does not look like SAML identity provider metadata. It needs an EntityDescriptor containing an IDPSSODescriptor -- Service Provider metadata and login pages are the two things most often pasted here by mistake.'
        );
        return;
      }
      request = { metadataXml: xml };
    }

    setImporting(true);
    try {
      const response = await importSamlMetadata(request);
      applyImportResult(response, mode);
    } catch (err) {
      // Persistent inline Alert, never a toast: the backend's diagnostics are
      // long, specific and the whole point of the endpoint.
      setImportError(describeImportFailure(err, mode));
    } finally {
      setImporting(false);
    }
  };

  /**
   * Re-fetch the recorded metadata descriptor and merge ONLY the certificate.
   *
   * Certificates are pinned rather than fetched at login time
   * (useMetadataDescriptorUrl is exposed nowhere), so rotation is a manual
   * re-import. Endpoints and identity mapping are left alone -- silently
   * re-pointing a working provider is not what "re-import certificate" says --
   * but a drift in either is surfaced inline.
   */
  const handleReimportCertificate = async () => {
    const url = (formData.metadataDescriptorUrl || editingIdP?.config?.metadataDescriptorUrl || '').trim();
    if (!url) return;

    setImportError(null);
    setImportInfo(null);
    setImporting(true);

    try {
      const response = await importSamlMetadata({ metadataUrl: url });
      const imported = response?.request || {};

      if (!imported.signingCertificate) {
        setImportError(
          'The document at the recorded metadata URL carries no signing certificate. Paste the new certificate manually, or re-import the full metadata document.'
        );
        return;
      }

      const warnings = [...(response?.warnings || [])];
      if (imported.idpEntityId && imported.idpEntityId !== formData.idpEntityId) {
        warnings.push(
          `The metadata now declares IdP Entity ID "${imported.idpEntityId}", which differs from the stored "${formData.idpEntityId}". Only the certificate was updated -- change the Entity ID yourself if the identity provider really moved.`
        );
      }
      if (
        imported.singleSignOnServiceUrl &&
        imported.singleSignOnServiceUrl !== formData.singleSignOnServiceUrl
      ) {
        warnings.push(
          `The metadata now declares Sign-on URL "${imported.singleSignOnServiceUrl}", which differs from the stored "${formData.singleSignOnServiceUrl}". Only the certificate was updated.`
        );
      }

      setFormData((prev) => ({ ...prev, signingCertificate: imported.signingCertificate }));
      setImportInfo({ mode: 'url', filled: ['Signing certificate'], warnings });
    } catch (err) {
      setImportError(describeImportFailure(err, 'url'));
    } finally {
      setImporting(false);
    }
  };

  /* Inline, never a toast: every one of these points at a specific field the
   * admin can see, and a toast disappears before they have finished reading it. */
  const findFormProblem = () => {
    if (!formData.displayName.trim()) {
      return 'Display Name is required. It is also what generates the provider alias.';
    }
    if (!formData.singleSignOnServiceUrl.trim()) {
      return 'Sign-on URL is required. Import your metadata, or paste the identity provider’s SAML 2.0 sign-on endpoint.';
    }
    if (!formData.idpEntityId.trim()) {
      return 'IdP Entity ID is required. Without it Prism performs no Issuer validation at all.';
    }
    if (formData.validateSignature && !formData.signingCertificate.trim()) {
      return 'A signing certificate is required while Validate Signature is on. Paste the certificate, or turn Validate Signature off in Advanced settings (not recommended).';
    }
    if (
      isAttributePrincipal(formData.principalType) &&
      !formData.principalAttribute.trim() &&
      !formData.emailAttributeName.trim()
    ) {
      return 'Principal Attribute is required when Principal Type is not Subject NameID. A null principal is rejected at login.';
    }
    if (
      !formData.emailAttributeName.trim() &&
      !(formData.principalType === PRINCIPAL_TYPE_SUBJECT && formData.nameIDPolicyFormat === NAMEID_FORMAT_EMAIL_ADDRESS)
    ) {
      return 'Email claim attribute name is required. Prism matches a federated login to an existing account by email, and only the Subject NameID + SAML 1.1 emailAddress pairing carries the email without a claim.';
    }
    if (byteLength(formData.metadataXml) > MAX_METADATA_BYTES) {
      return METADATA_LIMIT_MESSAGE;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const problem = findFormProblem();
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);

    const payload = buildSamlPayload(formData, { editing: !!editingIdP });

    try {
      if (editingIdP) {
        const updated = await updateSamlIdP(editingIdP.alias, payload);
        if (updated) {
          handleClose();
        }
      } else {
        const created = await createSamlIdP(payload);
        if (created) {
          handleClose();
          // The real alias -- and therefore the real ACS URL -- is only known
          // now. Hand it straight to the admin before they leave the page.
          setDetailsIdP(created);
          setDetailsJustCreated(true);
          setDetailsOpen(true);
        }
      }
    } catch (error) {
      setFormError(
        `Failed to ${editingIdP ? 'update' : 'create'} the SAML Identity Provider: ${error.message}`
      );
    }
  };

  const handleDelete = async (idp) => {
    await deleteSamlIdP(idp.alias);
    // The list is refreshed by the hook.
  };

  // Minimalist input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Metropolis, sans-serif',
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#E5E5E5',
      },
      '&:hover fieldset': {
        borderColor: accentColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: accentColor,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Metropolis, sans-serif',
      color: '#666666',
      '&.Mui-focused': {
        color: accentColor,
      },
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Metropolis, sans-serif',
    },
  };

  const switchStyle = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor },
  };

  const switchLabelStyle = {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Metropolis, sans-serif',
      fontSize: '0.875rem',
      color: '#253E66',
    },
  };

  const canAddMore = samlIdPs.length < samlIdPsLimit;
  const selectedNameIDFormat = SAML_NAMEID_FORMATS.find((f) => f.value === formData.nameIDPolicyFormat);
  const reimportUrl = (formData.metadataDescriptorUrl || editingIdP?.config?.metadataDescriptorUrl || '').trim();
  const busy = loading || importing;

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E5E5',
        borderRadius: '8px',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SamlIcon sx={{ fontSize: 32, color: accentColor }} />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  color: '#253E66',
                }}
              >
                SAML 2.0 Providers
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#999999',
                  fontFamily: 'Metropolis, sans-serif',
                }}
              >
                Federate a SAML 2.0 identity provider (Microsoft Entra ID, Okta, Google Workspace, ADFS) ({samlIdPs.length}/{samlIdPsLimit})
              </Typography>
            </Box>
          </Box>

          <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : ''}>
            <span>
              <Button
                variant="contained"
                onClick={handleOpenCreate}
                disabled={isReadOnly || loading || !canAddMore}
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: accentColor,
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: accentColorHover,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#E5E5E5',
                    color: '#999999',
                  },
                }}
              >
                Add SAML IdP
              </Button>
            </span>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2, borderColor: '#E5E5E5' }} />

        {/* Limit Warning */}
        {!canAddMore && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: '#FEF9E7',
              border: '1px solid #F39C1220',
              borderRadius: '6px',
              '& .MuiAlert-icon': { color: '#F39C12' },
              '& .MuiAlert-message': {
                fontFamily: 'Metropolis, sans-serif',
                color: '#253E66',
              },
            }}
          >
            Maximum number of SAML Identity Providers ({samlIdPsLimit}) reached.
          </Alert>
        )}

        {/* IdP List */}
        {isInitialLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={40} sx={{ color: accentColor, mb: 2 }} />
            <Typography
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                color: '#666666',
                fontWeight: 500,
              }}
            >
              Loading SAML 2.0 Providers...
            </Typography>
          </Box>
        ) : samlIdPs.length > 0 ? (
          <List sx={{ p: 0 }}>
            {samlIdPs.map((idp, index) => (
              <Paper
                key={idp.alias || index}
                elevation={0}
                sx={{
                  '@keyframes fadeInItem': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: `fadeInItem 0.3s ease-out ${index * 0.05}s both`,
                  mb: 2,
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <ListItem
                  sx={{
                    py: 2,
                    px: 3,
                    backgroundColor: '#F9FBFD',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            color: '#253E66',
                          }}
                        >
                          {idp.displayName || 'SAML 2.0'}
                        </Typography>
                        <Chip
                          label={idp.enabled !== false ? 'Enabled' : 'Disabled'}
                          size="small"
                          sx={{
                            backgroundColor: idp.enabled !== false ? '#E8F8F0' : '#FDEDEC',
                            color: idp.enabled !== false ? '#27AE60' : '#E74C3C',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#999999',
                            fontSize: '0.75rem',
                          }}
                        >
                          Alias: <span style={{ fontFamily: 'monospace', color: '#666666' }}>{idp.alias}</span>
                        </Typography>
                        {idp.config?.idpEntityId && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#999999',
                              fontSize: '0.75rem',
                              mt: 0.5,
                            }}
                          >
                            Entity ID: <span style={{ fontFamily: 'monospace', color: '#666666' }}>{idp.config.idpEntityId}</span>
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* Deliberately NOT disabled for read-only: these are the
                          values the admin has to paste into their identity
                          provider, and reading them changes nothing. */}
                      <Tooltip title="Connection details">
                        <span>
                          <IconButton
                            onClick={() => handleOpenDetails(idp)}
                            size="small"
                            sx={{
                              color: accentColor,
                              backgroundColor: accentColorLight,
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: '#E4DAF2',
                                borderColor: accentColor,
                              },
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Edit Configuration'}>
                        <span>
                          <IconButton
                            onClick={() => handleOpenEdit(idp)}
                            disabled={isReadOnly || loading}
                            size="small"
                            sx={{
                              color: accentColor,
                              backgroundColor: accentColorLight,
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: '#E4DAF2',
                                borderColor: accentColor,
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isReadOnly ? "Read-only access — you don't have permission to make changes." : 'Delete Configuration'}>
                        <span>
                          <IconButton
                            onClick={() => handleDelete(idp)}
                            disabled={isReadOnly || loading}
                            size="small"
                            sx={{
                              color: '#E74C3C',
                              backgroundColor: '#FDEDEC',
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              '&:hover': {
                                backgroundColor: '#FADBD8',
                                borderColor: '#E74C3C',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SamlIcon sx={{ fontSize: 64, color: '#E5E5E5', mb: 2 }} />
            <Typography
              variant="h6"
              sx={{
                color: '#999999',
                fontFamily: 'Bitter, Georgia, serif',
                mb: 1,
              }}
            >
              No SAML 2.0 Providers Configured
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#999999',
                fontFamily: 'Metropolis, sans-serif',
                mb: 3,
              }}
            >
              Federate Microsoft Entra ID, Okta, Google Workspace or ADFS over SAML 2.0.
            </Typography>
          </Box>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              border: '1px solid #E5E5E5',
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: 'Bitter, Georgia, serif',
              fontWeight: 600,
              color: '#253E66',
              borderBottom: '1px solid #E5E5E5',
              pb: 2,
            }}
          >
            {editingIdP ? 'Edit SAML 2.0 Provider' : 'Create SAML 2.0 Provider'}
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {/* Service Provider values */}
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: accentColorLight,
                  border: `1px solid ${accentColor}20`,
                  borderRadius: '8px',
                  p: 3,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <InfoIcon sx={{ color: accentColor, fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      color: accentColor,
                      fontFamily: 'Bitter, Georgia, serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {editingIdP
                      ? 'Connection details'
                      : 'Preview — final URLs are confirmed after you create the provider'}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#253E66',
                    fontFamily: 'Metropolis, sans-serif',
                    mb: 2,
                  }}
                >
                  Paste these into your identity provider. Alias:{' '}
                  <span style={{ fontFamily: 'monospace' }}>{previewAlias || 'saml'}</span>
                </Typography>

                {!editingIdP && formData.displayName && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#F39C12',
                      fontFamily: 'Metropolis, sans-serif',
                      fontStyle: 'italic',
                      display: 'block',
                      mb: 2,
                    }}
                  >
                    Note: A numeric suffix may be added if this alias already exists. The Reply URL below would change with it, so re-check it after creating.
                  </Typography>
                )}

                <CopyRow
                  label="Reply URL (Assertion Consumer Service URL)"
                  hint="Entra ID: Single sign-on → Basic SAML Configuration → Reply URL."
                  value={dialogAcsUrl}
                  copied={copiedKey === 'acsUrl'}
                  onCopy={() => handleCopy('acsUrl', dialogAcsUrl)}
                />

                <CopyRow
                  label="Identifier (Entity ID)"
                  hint="Entra ID: Single sign-on → Basic SAML Configuration → Identifier (Entity ID)."
                  value={dialogSpEntityId}
                  copied={copiedKey === 'spEntityId'}
                  onCopy={() => handleCopy('spEntityId', dialogSpEntityId)}
                />

                <CopyRow
                  label="SP metadata URL"
                  hint="Public and unauthenticated, but only resolves once the provider has been saved."
                  value={dialogDescriptorUrl}
                  copied={copiedKey === 'descriptorUrl'}
                  onCopy={() => handleCopy('descriptorUrl', dialogDescriptorUrl)}
                  external={!!editingIdP}
                />
              </Paper>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
              >
                {/* Display Name */}
                <TextField
                  label="Display Name"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder="e.g., Contoso Entra"
                  required
                  fullWidth
                  size="small"
                  helperText="A unique name for this SAML provider. Used to generate the alias and shown on the login page."
                  sx={inputStyle}
                />

                {/* Configuration source */}
                <FormControl>
                  <FormLabel
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: '#666666',
                      '&.Mui-focused': { color: '#666666' },
                    }}
                  >
                    Configuration source
                  </FormLabel>
                  <RadioGroup row name="configurationSource" value={sourceMode} onChange={handleSourceChange}>
                    {CONFIGURATION_SOURCES.map((source) => (
                      <FormControlLabel
                        key={source.value}
                        value={source.value}
                        control={<Radio size="small" sx={{ '&.Mui-checked': { color: accentColor } }} />}
                        label={source.label}
                        disabled={importing}
                        sx={switchLabelStyle}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>

                {sourceMode === 'url' && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Metadata URL"
                      name="metadataUrl"
                      value={formData.metadataUrl}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      placeholder="https://login.microsoftonline.com/{tenantId}/federationmetadata/2007-06/federationmetadata.xml?appid={appId}"
                      helperText="Use the per-application ?appid= federation metadata URL. The tenant-wide URL does not carry the application's signing certificate."
                      sx={inputStyle}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleImport}
                      disabled={importing || !formData.metadataUrl.trim()}
                      sx={{
                        borderColor: '#E5E5E5',
                        color: accentColor,
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '6px',
                        minWidth: 120,
                        height: 40,
                        '&:hover': {
                          borderColor: accentColor,
                          backgroundColor: accentColorLight,
                        },
                      }}
                      startIcon={
                        importing ? <CircularProgress size={16} sx={{ color: accentColor }} /> : <ImportIcon />
                      }
                    >
                      {importing ? 'Importing...' : 'Import'}
                    </Button>
                  </Box>
                )}

                {sourceMode === 'xml' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        component="label"
                        variant="outlined"
                        disabled={importing}
                        startIcon={<UploadFileIcon />}
                        sx={{
                          borderColor: '#E5E5E5',
                          color: accentColor,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '6px',
                          height: 40,
                          '&:hover': {
                            borderColor: accentColor,
                            backgroundColor: accentColorLight,
                          },
                        }}
                      >
                        Choose XML file
                        <input
                          type="file"
                          accept=".xml,text/xml,application/xml"
                          hidden
                          onChange={onFile}
                        />
                      </Button>

                      <Box sx={{ flex: 1 }} />

                      <Button
                        variant="outlined"
                        onClick={handleImport}
                        disabled={importing || !formData.metadataXml.trim()}
                        sx={{
                          borderColor: '#E5E5E5',
                          color: accentColor,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '6px',
                          minWidth: 120,
                          height: 40,
                          '&:hover': {
                            borderColor: accentColor,
                            backgroundColor: accentColorLight,
                          },
                        }}
                        startIcon={
                          importing ? <CircularProgress size={16} sx={{ color: accentColor }} /> : <ImportIcon />
                        }
                      >
                        {importing ? 'Importing...' : 'Import'}
                      </Button>
                    </Box>

                    <TextField
                      label="Metadata XML"
                      name="metadataXml"
                      value={formData.metadataXml}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      multiline
                      rows={8}
                      placeholder="<EntityDescriptor xmlns=&quot;urn:oasis:names:tc:SAML:2.0:metadata&quot; ...>"
                      helperText={`Paste the document or load it from a file, then choose Import. Maximum ${Math.floor(
                        MAX_METADATA_BYTES / 1024
                      )} KB.`}
                      sx={{
                        ...inputStyle,
                        '& .MuiOutlinedInput-root': {
                          ...inputStyle['& .MuiOutlinedInput-root'],
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                        },
                      }}
                    />
                  </Box>
                )}

                {/* Import failures are a persistent inline Alert, never a toast. */}
                {importError && (
                  <Alert
                    severity="error"
                    onClose={() => setImportError(null)}
                    sx={{
                      backgroundColor: '#FDEDEC',
                      border: '1px solid #E74C3C20',
                      borderRadius: '6px',
                      '& .MuiAlert-icon': { color: '#E74C3C' },
                      '& .MuiAlert-message': {
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#253E66',
                      },
                    }}
                  >
                    <AlertTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                      Import failed
                    </AlertTitle>
                    {importError}
                  </Alert>
                )}

                {importInfo && (
                  <Alert
                    severity={importInfo.warnings.length > 0 ? 'warning' : 'success'}
                    onClose={() => setImportInfo(null)}
                    sx={{
                      backgroundColor: importInfo.warnings.length > 0 ? '#FEF9E7' : '#E8F8F0',
                      border: `1px solid ${importInfo.warnings.length > 0 ? '#F39C12' : '#27AE60'}20`,
                      borderRadius: '6px',
                      '& .MuiAlert-icon': {
                        color: importInfo.warnings.length > 0 ? '#F39C12' : '#27AE60',
                      },
                      '& .MuiAlert-message': {
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#253E66',
                      },
                    }}
                  >
                    <AlertTitle sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600 }}>
                      {importInfo.filled.length > 0
                        ? `Imported: ${importInfo.filled.join(', ')}`
                        : 'The metadata document filled in nothing new'}
                    </AlertTitle>
                    {importInfo.warnings.length > 0 && (
                      <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                        {importInfo.warnings.map((warning) => (
                          <Typography
                            key={warning}
                            component="li"
                            variant="body2"
                            sx={{ fontFamily: 'Metropolis, sans-serif' }}
                          >
                            {warning}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Alert>
                )}

                {/* Sign-on URL */}
                <TextField
                  label="Sign-on URL"
                  name="singleSignOnServiceUrl"
                  value={formData.singleSignOnServiceUrl}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  size="small"
                  placeholder="https://login.microsoftonline.com/{tenantId}/saml2"
                  helperText="The identity provider's SAML 2.0 SSO endpoint. Must be https."
                  sx={inputStyle}
                />

                {/* IdP Entity ID */}
                <TextField
                  label="IdP Entity ID"
                  name="idpEntityId"
                  value={formData.idpEntityId}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  size="small"
                  placeholder="https://sts.windows.net/{tenantId}/"
                  helperText="The Issuer the identity provider puts in its assertions. Entra's ends with a trailing slash — keep it, it is significant."
                  sx={inputStyle}
                />

                {/* Signing certificate */}
                <Box>
                  <TextField
                    label="Signing Certificate"
                    name="signingCertificate"
                    value={formData.signingCertificate}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    placeholder="MIIC8DCCAdigAwIBAgIQ..."
                    helperText="PEM headers and line breaks are stripped automatically. Comma-separate two certificates to cover a rotation window."
                    sx={{
                      ...inputStyle,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyle['& .MuiOutlinedInput-root'],
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                      },
                    }}
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1 }}>
                    {fingerprints.map((fingerprint) => (
                      <Chip
                        key={fingerprint}
                        icon={<FingerprintIcon />}
                        label={`Thumbprint: ${fingerprint}`}
                        size="small"
                        sx={{
                          backgroundColor: accentColorLight,
                          color: accentColorHover,
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          '& .MuiChip-icon': { color: accentColor },
                        }}
                      />
                    ))}

                    {editingIdP && reimportUrl && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleReimportCertificate}
                        disabled={importing}
                        startIcon={
                          importing ? <CircularProgress size={14} sx={{ color: accentColor }} /> : <AutorenewIcon />
                        }
                        sx={{
                          color: accentColor,
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Re-import signing certificate
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Email claim attribute name */}
                <TextField
                  label="Email claim attribute name"
                  name="emailAttributeName"
                  value={formData.emailAttributeName}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  helperText="The assertion attribute carrying the user's email. Prism links a federated login to an existing account by email — it never creates one."
                  sx={inputStyle}
                />

                {/* Advanced */}
                <Accordion
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E5E5',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    '&.Mui-expanded': { margin: 0 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#999999' }} />}
                    sx={{ minHeight: 56, '&.Mui-expanded': { minHeight: 56 }, px: 2.5 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: '#253E66',
                      }}
                    >
                      Advanced settings
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                    <Divider sx={{ mb: 2, borderColor: '#F0F0F0' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      <TextField
                        label="SP Entity ID override"
                        name="entityId"
                        value={formData.entityId}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        helperText="Leave blank to advertise the realm URL shown above. Override only if your identity provider requires a different Identifier."
                        sx={inputStyle}
                      />

                      <FormControl fullWidth size="small" sx={inputStyle}>
                        <InputLabel id="saml-nameid-format-label">NameID Policy Format</InputLabel>
                        <Select
                          labelId="saml-nameid-format-label"
                          label="NameID Policy Format"
                          name="nameIDPolicyFormat"
                          value={formData.nameIDPolicyFormat}
                          onChange={handleInputChange}
                        >
                          {SAML_NAMEID_FORMATS.map((format) => (
                            <MenuItem
                              key={format.value}
                              value={format.value}
                              sx={{ fontFamily: 'Metropolis, sans-serif' }}
                            >
                              {format.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {selectedNameIDFormat && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'Metropolis, sans-serif',
                              color: '#666666',
                              mt: 0.5,
                              ml: 1.75,
                            }}
                          >
                            {selectedNameIDFormat.helperText}
                          </Typography>
                        )}
                      </FormControl>

                      <FormControl fullWidth size="small" sx={inputStyle}>
                        <InputLabel id="saml-principal-type-label">Principal Type</InputLabel>
                        <Select
                          labelId="saml-principal-type-label"
                          label="Principal Type"
                          name="principalType"
                          value={formData.principalType}
                          onChange={handleInputChange}
                        >
                          {SAML_PRINCIPAL_TYPES.map((type) => (
                            <MenuItem
                              key={type.value}
                              value={type.value}
                              sx={{ fontFamily: 'Metropolis, sans-serif' }}
                            >
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            mt: 0.5,
                            ml: 1.75,
                          }}
                        >
                          Where the username comes from. Changing this on an existing provider orphans every federated link.
                        </Typography>
                      </FormControl>

                      {isAttributePrincipal(formData.principalType) && (
                        <TextField
                          label="Principal Attribute"
                          name="principalAttribute"
                          value={formData.principalAttribute}
                          onChange={handleInputChange}
                          fullWidth
                          size="small"
                          helperText="Required when the principal is not the Subject NameID. For Entra ID use the UPN claim — it is present for every user, while the email claim is empty for mailbox-less users and B2B guests."
                          sx={inputStyle}
                        />
                      )}

                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="validateSignature"
                              checked={formData.validateSignature}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="Validate signature"
                          sx={switchLabelStyle}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              name="wantAssertionsSigned"
                              checked={formData.wantAssertionsSigned}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="Want assertions signed"
                          sx={switchLabelStyle}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              name="wantAuthnRequestsSigned"
                              checked={formData.wantAuthnRequestsSigned}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="Want AuthnRequests signed"
                          sx={switchLabelStyle}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              name="forceAuthn"
                              checked={formData.forceAuthn}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="Force re-authentication at the identity provider"
                          sx={switchLabelStyle}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              name="postBindingResponse"
                              checked={formData.postBindingResponse}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="POST binding for responses"
                          sx={switchLabelStyle}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              name="postBindingAuthnRequest"
                              checked={formData.postBindingAuthnRequest}
                              onChange={handleInputChange}
                              sx={switchStyle}
                            />
                          }
                          label="POST binding for AuthnRequests"
                          sx={switchLabelStyle}
                        />
                      </Box>

                      <FormControl fullWidth size="small" sx={inputStyle}>
                        <InputLabel id="saml-signature-algorithm-label">Signature Algorithm</InputLabel>
                        <Select
                          labelId="saml-signature-algorithm-label"
                          label="Signature Algorithm"
                          name="signatureAlgorithm"
                          value={formData.signatureAlgorithm}
                          onChange={handleInputChange}
                        >
                          {SAML_SIGNATURE_ALGORITHMS.map((algorithm) => (
                            <MenuItem
                              key={algorithm}
                              value={algorithm}
                              sx={{ fontFamily: 'Metropolis, sans-serif' }}
                            >
                              {algorithm}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            mt: 0.5,
                            ml: 1.75,
                          }}
                        >
                          RSA_SHA256 is the only algorithm Entra ID verifies.
                        </Typography>
                      </FormControl>

                      <TextField
                        label="Allowed Clock Skew (seconds)"
                        name="allowedClockSkew"
                        type="number"
                        value={formData.allowedClockSkew}
                        onChange={handleInputChange}
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, max: 300 }}
                        helperText="0–300. Entra adds no buffer to NotBefore, so a small allowance avoids spurious failures on clock drift."
                        sx={inputStyle}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Inline submit blocker */}
                {formError && (
                  <Alert
                    severity="error"
                    onClose={() => setFormError(null)}
                    sx={{
                      backgroundColor: '#FDEDEC',
                      border: '1px solid #E74C3C20',
                      borderRadius: '6px',
                      '& .MuiAlert-icon': { color: '#E74C3C' },
                      '& .MuiAlert-message': {
                        fontFamily: 'Metropolis, sans-serif',
                        color: '#253E66',
                      },
                    }}
                  >
                    {formError}
                  </Alert>
                )}
              </Box>
            </motion.div>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E5E5E5' }}>
            <Box sx={{ flex: 1 }} />

            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={busy}
              sx={{
                borderColor: '#E5E5E5',
                color: '#666666',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: '#999999',
                  backgroundColor: '#F9FBFD',
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isReadOnly ||
                busy ||
                !formData.displayName.trim() ||
                !formData.singleSignOnServiceUrl.trim() ||
                !formData.idpEntityId.trim()
              }
              sx={{
                backgroundColor: accentColor,
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '6px',
                px: 3,
                '&:hover': {
                  backgroundColor: accentColorHover,
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E5E5E5',
                  color: '#999999',
                },
              }}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SamlIcon sx={{ fontSize: 16 }} />
              }
            >
              {loading ? 'Saving...' : editingIdP ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Post-create handoff / on-demand connection details */}
        <SamlConnectionDetails
          open={detailsOpen}
          onClose={handleCloseDetails}
          idp={detailsIdP}
          justCreated={detailsJustCreated}
        />
      </CardContent>
    </Card>
  );
};

export default SamlIdPList;
